import React from "react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import styled from "styled-components";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import { isValid } from "date-fns";
import {
    detectChartGranularity,
    formatChartTooltipDate,
    formatChartAxisDate,
} from "src/utils/chart-date-formatter";
import calculateInterval from "src/components/calculate-tick-interval";
import { Stack } from "@mui/material";
import { resolveObjPathString } from "src/utils/resolve-path";
import ChartColors from "src/components/chart-colors";
import getRandomColor from "src/components/random-color-generator";
import { resolveName } from "src/utils/templating";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: ${({ color }) => color};
`;

const TooltipContent = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const CustomTooltip = ({ active, payload, granularity }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer className="failures-perstage-tooltip">
                <TooltipTitle>
                    {isValid(new Date(payload[0].payload.datetime)) &&
                        formatChartTooltipDate(payload[0].payload.datetime, granularity)}
                </TooltipTitle>
                {payload
                    .slice()
                    .reverse()
                    .map((entry) => (
                        <Stack direction="row" spacing={0.5}>
                            <TooltipContent color={entry.color}>{entry.name}: </TooltipContent>
                            <TooltipContent> {entry.value}</TooltipContent>
                        </Stack>
                    ))}
            </TooltipContainer>
        );
    }
    return null;
};

const handleColors = (index) => {
    return ChartColors[index] || getRandomColor();
};

const AreaChartTickets = ({ data, parameters, context }) => {
    const [interval, setInterval] = React.useState(0);
    const [opacity, setOpacity] = React.useState({});
    const [visibility, setVisibility] = React.useState({});

    React.useEffect(() => {
        setInterval(calculateInterval({ data }));
    }, []);

    React.useEffect(() => {
        if (parameters.yValues) {
            const tempVis = {};
            const tempOpac = {};
            parameters.yValues.forEach((yValue) => {
                tempVis[yValue.dataKey] = true;
                tempOpac[yValue.dataKey] = 1;
            });
            setVisibility(tempVis);
            setOpacity(tempVis);
        }
    }, [parameters && JSON.stringify(parameters)]);

    const handleMouseEnter = (e) => {
        const { dataKey } = e;
        setOpacity((prevState) =>
            Object.keys(prevState).reduce((acc, curr) => {
                if (curr === dataKey) {
                    acc[curr] = 1;
                } else {
                    acc[curr] = 0.5;
                }
                return acc;
            }, {})
        );
    };

    const handleMouseLeave = () => {
        setOpacity((prevState) =>
            Object.keys(prevState).reduce((acc, curr) => {
                acc[curr] = 1;
                return acc;
            }, {})
        );
    };

    const selectBar = (e) => {
        setVisibility((prevState) => ({
            ...prevState,
            [e.dataKey]: !prevState[e.dataKey],
        }));
    };

    const granularity = detectChartGranularity(data);

    const formattedData = (data || []).map((item) => {
        const dataPoint = {};
        parameters.xValues.forEach((xValue) => {
            dataPoint[xValue.dataKey] = item[xValue.dataKey];
            if (xValue.type === "datetime") {
                dataPoint.date =
                    isValid(new Date(item[xValue.dataKey])) &&
                    formatChartAxisDate(item[xValue.dataKey], granularity);
            }
        });

        parameters.yValues.forEach((yValue) => {
            dataPoint[yValue.dataKey] = resolveObjPathString(
                item,
                resolveName(yValue.dataKey, context)
            );
        });

        return dataPoint;
    });

    if (!formattedData || formattedData.length === 0) return <NoDataPlaceholder />;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart
                width={500}
                height={400}
                data={formattedData}
                margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                {parameters.xValues &&
                    parameters.xValues.map((xValue) => (
                        <XAxis
                            dataKey={xValue.type === "datetime" ? "date" : xValue.dataKey}
                            interval={interval}
                            tick={{
                                fill: "#EAF0F4",
                                opacity: "0.3",
                                fontSize: "12px",
                                fontWeight: "300",
                            }}
                        />
                    ))}
                <Tooltip content={<CustomTooltip granularity={granularity} />} />
                {parameters.yValues &&
                    parameters.yValues.map((yValue, index) => (
                        <>
                            <Area
                                yAxisId={yValue.axis?.id}
                                key={yValue.dataKey}
                                type={yValue.type || "monotone"}
                                dataKey={yValue.dataKey}
                                name={yValue.name}
                                stackId={yValue.stackId}
                                stroke={yValue.stroke || handleColors(index)}
                                fill={yValue.fill || handleColors(index)}
                                fillOpacity={opacity[yValue.dataKey]}
                                hide={!visibility[yValue.dataKey]}
                            />
                            {yValue.axis && (
                                <YAxis
                                    yAxisId={yValue.axis?.id}
                                    orientation={yValue.axis?.orientation}
                                    tick={false}
                                    axisLine={false}
                                    tickLine={false}
                                    width={0}
                                />
                            )}
                        </>
                    ))}
                {parameters.referenceLine && (
                    <ReferenceLine
                        {...(parameters.referenceLine.axis &&
                            parameters.referenceLine.axis === "x" && {
                                x: parameters.referenceLine.value,
                            })}
                        {...(parameters.referenceLine.axis &&
                            parameters.referenceLine.axis === "y" && {
                                y: parameters.referenceLine.value,
                            })}
                        stroke={parameters.referenceLine.stroke}
                        strokeDasharray={parameters.referenceLine.strokeDasharray}
                        label={parameters.referenceLine.label}
                        position={parameters.referenceLine.position}
                        yAxisId={parameters.referenceLine.axisId}
                    />
                )}
                <Legend
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={selectBar}
                    wrapperStyle={{ fontSize: "11px", cursor: "pointer", marginBottom: "8px" }}
                    verticalAlign="bottom"
                    iconSize={8}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default AreaChartTickets;
